import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { BookOpen, Check, X, AlertCircle, Lock, Eye, EyeOff, ArrowLeft, Mail, ShieldCheck, Loader } from 'lucide-react';

const AuthActionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // Shared state
  const [status, setStatus] = useState('loading'); // loading, success, error, form
  const [errorMessage, setErrorMessage] = useState('');

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!mode || !oobCode) {
      setStatus('error');
      setErrorMessage('Invalid or missing action link. Please try again from your email.');
      return;
    }

    if (mode === 'verifyEmail') {
      handleVerifyEmail();
    } else if (mode === 'resetPassword') {
      handleVerifyResetCode();
    } else if (mode === 'recoverEmail') {
      handleRecoverEmail();
    } else {
      setStatus('error');
      setErrorMessage('Unknown action type.');
    }
  }, [mode, oobCode]);

  const handleVerifyEmail = async () => {
    try {
      await applyActionCode(auth, oobCode);
      // Reload current user if logged in so emailVerified updates
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      setStatus('success');
    } catch (error) {
      console.error('Email verification error:', error);
      if (error.code === 'auth/expired-action-code') {
        setErrorMessage('This verification link has expired. Please request a new one.');
      } else if (error.code === 'auth/invalid-action-code') {
        setErrorMessage('This link has already been used or is invalid. Please request a new verification email.');
      } else {
        setErrorMessage('Failed to verify your email. Please try again.');
      }
      setStatus('error');
    }
  };

  const handleVerifyResetCode = async () => {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode);
      setResetEmail(email);
      setStatus('form');
    } catch (error) {
      console.error('Reset code verification error:', error);
      if (error.code === 'auth/expired-action-code') {
        setErrorMessage('This password reset link has expired. Please request a new one.');
      } else if (error.code === 'auth/invalid-action-code') {
        setErrorMessage('This link has already been used or is invalid. Please request a new password reset.');
      } else {
        setErrorMessage('Invalid password reset link. Please try again.');
      }
      setStatus('error');
    }
  };

  const handleRecoverEmail = async () => {
    try {
      await applyActionCode(auth, oobCode);
      setStatus('success');
    } catch (error) {
      console.error('Email recovery error:', error);
      setErrorMessage('Failed to recover your email. The link may have expired.');
      setStatus('error');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/expired-action-code') {
        setErrorMessage('This reset link has expired. Please request a new one.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use at least 6 characters.');
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    if (mode === 'verifyEmail') return 'Email Verification';
    if (mode === 'resetPassword') return 'Reset Password';
    if (mode === 'recoverEmail') return 'Email Recovery';
    return 'Account Action';
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 relative overflow-hidden">
      {/* Decorative Circles */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition text-sm font-bold"
        >
          <ArrowLeft size={16} />
          Back to Home
        </button>
      </div>

      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-12 relative z-10">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-8 md:p-10 relative overflow-hidden">
            {/* Subtle Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, #9333ea 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} />
            </div>

            <div className="relative z-10">
              {/* Logo */}
              <div className="text-center mb-6">
                <img src="/assets/brlogo.webp" alt="BrightReading Logo" className="h-16 w-auto object-contain mx-auto mb-4" />
                <h2 className="text-2xl font-black text-gray-900">{getTitle()}</h2>
              </div>

              {/* Loading State */}
              {status === 'loading' && (
                <div className="text-center py-8">
                  <Loader size={48} className="mx-auto text-purple-500 animate-spin mb-4" />
                  <p className="text-gray-600 font-semibold text-lg">
                    {mode === 'verifyEmail' && 'Verifying your email...'}
                    {mode === 'resetPassword' && 'Validating reset link...'}
                    {mode === 'recoverEmail' && 'Recovering your email...'}
                    {!mode && 'Processing...'}
                  </p>
                </div>
              )}

              {/* Success — Email Verified */}
              {status === 'success' && mode === 'verifyEmail' && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 border-3 sm:border-4 border-green-200">
                    <Check size={40} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Email Verified!</h3>
                  <p className="text-gray-600 font-semibold mb-8">
                    Your email has been confirmed. You're all set!
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition transform hover:scale-[1.03] active:scale-95 border-3 sm:border-4 border-white shadow-lg"
                    >
                      Go to Dashboard
                    </button>
                    <button
                      onClick={() => navigate('/upgrade')}
                      className="w-full bg-purple-50 hover:bg-purple-100 text-purple-600 py-4 rounded-2xl font-black text-lg transition border-2 border-purple-200"
                    >
                      View Premium Plans
                    </button>
                  </div>
                </div>
              )}

              {/* Success — Password Reset */}
              {status === 'success' && mode === 'resetPassword' && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 border-3 sm:border-4 border-green-200">
                    <ShieldCheck size={40} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Password Updated!</h3>
                  <p className="text-gray-600 font-semibold mb-8">
                    Your password has been reset successfully. You can now sign in with your new password.
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition transform hover:scale-[1.03] active:scale-95 border-3 sm:border-4 border-white shadow-lg"
                  >
                    Sign In Now
                  </button>
                </div>
              )}

              {/* Success — Email Recovery */}
              {status === 'success' && mode === 'recoverEmail' && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 border-3 sm:border-4 border-green-200">
                    <Mail size={40} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Email Recovered!</h3>
                  <p className="text-gray-600 font-semibold mb-8">
                    Your email address has been restored. You may want to change your password for security.
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition transform hover:scale-[1.03] active:scale-95 border-3 sm:border-4 border-white shadow-lg"
                  >
                    Sign In
                  </button>
                </div>
              )}

              {/* Error State */}
              {status === 'error' && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5 border-3 sm:border-4 border-red-200">
                    <X size={40} className="text-red-600" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Something Went Wrong</h3>
                  <p className="text-gray-600 font-semibold mb-8">{errorMessage}</p>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition transform hover:scale-[1.03] active:scale-95 border-3 sm:border-4 border-white shadow-lg"
                    >
                      Go to Login
                    </button>
                    <button
                      onClick={() => navigate('/')}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 rounded-2xl font-black text-lg transition border-2 border-gray-200"
                    >
                      Back to Home
                    </button>
                  </div>
                </div>
              )}

              {/* Password Reset Form */}
              {status === 'form' && mode === 'resetPassword' && (
                <div className="py-2">
                  <div className="bg-purple-50 rounded-2xl p-4 mb-6 border-2 border-purple-200">
                    <p className="text-sm text-gray-600 font-semibold">
                      Resetting password for
                    </p>
                    <p className="font-black text-purple-600 text-lg">{resetEmail}</p>
                  </div>

                  {errorMessage && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
                      <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                        <AlertCircle className="text-red-600" size={20} />
                      </div>
                      <p className="text-red-700 font-semibold text-sm">{errorMessage}</p>
                    </div>
                  )}

                  <form onSubmit={handlePasswordReset} className="space-y-5">
                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                        <Lock size={16} className="text-purple-500" />
                        New Password
                      </label>
                      <div className="relative group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-lg font-semibold group-hover:border-purple-400"
                          placeholder="Enter new password"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-600 transition p-1 rounded-lg hover:bg-purple-50"
                        >
                          {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                        <Lock size={16} className="text-purple-500" />
                        Confirm New Password
                      </label>
                      <div className="relative group">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          className="w-full px-4 py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-lg font-semibold group-hover:border-purple-400"
                          placeholder="Confirm new password"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-600 transition p-1 rounded-lg hover:bg-purple-50"
                        >
                          {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-5 rounded-2xl font-black text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-2 border-3 sm:border-4 border-white shadow-lg"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <ShieldCheck size={22} />
                          Set New Password
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthActionPage;
